import { PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT } from 'constants/constants';
import { DTOgetOrderDetailedData, ORDER_STATUS, PAYMENT_TYPES, TYPES_OF_BILLING } from 'types/orders';
import { IlogisticServiceDetails, LIST_OF_QUOTE_STATUSES } from 'types/services';
import { LOGISTIC_SERVICE_STATUS_NAME } from 'types/suscription/history.types';
import { PAYMENT_STATUS } from './constants';

const PAYMENT_METHODS_BY_TRANSFER = [
  PAYMENT_TYPES.TRANSFER_MXN,
  PAYMENT_TYPES.TRANSFER_USD,
  PAYMENT_TYPES.TRANSFER_COP,
  PAYMENT_TYPES.TRANSFER_PEN,
  PAYMENT_TYPES.TRANSFER_ARS,
  PAYMENT_TYPES.TRANSFER_CLP,
  PAYMENT_TYPES.TRANSFER_BRL
];

const PAYMENT_METHODS_BY_CREDIT_CARD = [
  PAYMENT_TYPES.CARD_COP,
  PAYMENT_TYPES.CARD_USD_LLC,
  PAYMENT_TYPES.CARD_USD_CORP
];

const PAYMENT_METHODS_BY_CREDIT_BORD = [
  PAYMENT_TYPES.BORD_CREDIT_MXN,
  PAYMENT_TYPES.BORD_CREDIT_USD,
  PAYMENT_TYPES.BORD_CREDIT_COP,
  PAYMENT_TYPES.BORD_CREDIT_PEN,
  PAYMENT_TYPES.BORD_CREDIT_ARS,
  PAYMENT_TYPES.BORD_CREDIT_CLP,
  PAYMENT_TYPES.BORD_CREDIT_BRL
];

export const getUIStateForOrderView = (order?: DTOgetOrderDetailedData) => {
  try {
    const orderIsCancelled = order?.statusName === ORDER_STATUS.CANCELLED;

    const { paymentMethod, committedPaymentDate } = order?.generalData?.paymentData ?? {};

    const canSeePaymentDataButton =
      (PAYMENT_METHODS_BY_TRANSFER.includes(paymentMethod as PAYMENT_TYPES) ||
        PAYMENT_METHODS_BY_CREDIT_BORD.includes(paymentMethod as PAYMENT_TYPES)) &&
      order?.generalData?.bankAccountInfo &&
      Boolean(Object.keys(order?.generalData?.bankAccountInfo).length);

    const thePaymentWasMadeByCard = PAYMENT_METHODS_BY_CREDIT_CARD.includes(paymentMethod as PAYMENT_TYPES);

    const hasPaymentAgreement = committedPaymentDate && !thePaymentWasMadeByCard;

    const thePaymentWasMadeBordCredit = PAYMENT_METHODS_BY_CREDIT_BORD.includes(paymentMethod as PAYMENT_TYPES);

    const thePaymentIsInternationalOrCOPCard = PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT.includes(
      paymentMethod as PAYMENT_TYPES
    );

    return {
      showStatus: !orderIsCancelled,
      showPaymentDataButton: canSeePaymentDataButton && !orderIsCancelled,
      showCommittedPaymentDate: hasPaymentAgreement && !orderIsCancelled,
      showPaymentAgreementAlert: thePaymentWasMadeBordCredit && !orderIsCancelled,
      showPaymentLink: thePaymentIsInternationalOrCOPCard && !orderIsCancelled,
      disablePaymentUploader: orderIsCancelled,
      showPaymentUploader: true
    };
  } catch (error) {
    return {
      showStatus: false,
      showPaymentDataButton: false,
      showCommittedPaymentDate: false,
      showPaymentAgreementAlert: false,
      showPaymentLink: false,
      disablePaymentUploader: false,
      showPaymentUploader: false
    };
  }
};

export const getUIStateForLogisticServiceView = (logisticService?: IlogisticServiceDetails) => {
  try {
    const { statusName, paymentMethod, paymentAgreementDate, bankAccountInfo } = logisticService ?? {};

    const paymentStatus = calcStatusForLogisticService(logisticService!);

    const paymentIsByCare = paymentStatus === PAYMENT_STATUS.covered_with_care;

    const isCancelled = statusName === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED;

    const canSeePaymentDataButton =
      (PAYMENT_METHODS_BY_TRANSFER.includes(paymentMethod as PAYMENT_TYPES) ||
        PAYMENT_METHODS_BY_CREDIT_BORD.includes(paymentMethod as PAYMENT_TYPES)) &&
      bankAccountInfo &&
      Boolean(Object.keys(bankAccountInfo).length);

    const hasPaymentAgreement = Boolean(paymentAgreementDate);

    const thePaymentWasMadeBordCredit = PAYMENT_METHODS_BY_CREDIT_BORD.includes(paymentMethod as PAYMENT_TYPES);

    const isPaymentMethodToShowLink = PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT.includes(
      paymentMethod as PAYMENT_TYPES
    );

    return {
      showStatus: !isCancelled,
      showPaymentDataButton: canSeePaymentDataButton && !isCancelled && !paymentIsByCare,
      showCommittedPaymentDate: hasPaymentAgreement && !isCancelled && !paymentIsByCare,
      showPaymentAgreementAlert: thePaymentWasMadeBordCredit && !isCancelled && !paymentIsByCare,
      showPaymentLink: isPaymentMethodToShowLink && !isCancelled && !paymentIsByCare,
      disablePaymentUploader: isCancelled,
      showPaymentUploader: !paymentIsByCare
    };
  } catch (error) {
    return {
      showStatus: false,
      showPaymentDataButton: false,
      showCommittedPaymentDate: false,
      showPaymentAgreementAlert: false,
      showPaymentLink: false,
      disablePaymentUploader: false,
      showPaymentUploader: false
    };
  }
};

export const calcStatusForOrder = (orderData: DTOgetOrderDetailedData) => {
  if (orderData?.orderDates?.paymentDate || orderData.generalData?.paymentData?.proofOfPayment) {
    return PAYMENT_STATUS.paid;
  }

  if (orderData?.generalData?.paymentData?.committedPaymentDate) {
    const committedDate = new Date(orderData.generalData.paymentData.committedPaymentDate);
    const currentDate = new Date();

    return committedDate < currentDate ? PAYMENT_STATUS.overdue : PAYMENT_STATUS.scheduled;
  }

  return PAYMENT_STATUS.peding;
};

export const calcStatusForLogisticService = (logisticServiceData: IlogisticServiceDetails) => {
  const { paymentAgreementDate, isPaymentAgreement, isPaymentReceived, manualQuote, quotesLog, paymentType } =
    logisticServiceData ?? {};

  const isRejectedOrApprovedQuote =
    quotesLog?.status === LIST_OF_QUOTE_STATUSES.REJECTED || quotesLog?.status === LIST_OF_QUOTE_STATUSES.APPROVED;

  if (paymentType === 'Care') {
    return PAYMENT_STATUS.covered_with_care;
  }

  if (isPaymentReceived) {
    return PAYMENT_STATUS.paid;
  }

  if (!isPaymentReceived && manualQuote && !isRejectedOrApprovedQuote) {
    return PAYMENT_STATUS.unavailable;
  }

  if (isPaymentAgreement && paymentAgreementDate) {
    const committedDate = new Date(paymentAgreementDate);
    const currentDate = new Date();

    return committedDate < currentDate ? PAYMENT_STATUS.overdue : PAYMENT_STATUS.scheduled;
  }

  return PAYMENT_STATUS.peding;
};

export const getBillingTypePrefix = ({
  paymentMethod,
  billingProvider,
  billingCountryName
}: {
  paymentMethod?: PAYMENT_TYPES;
  billingProvider?: TYPES_OF_BILLING;
  billingCountryName?: string;
}) => {
  if (paymentMethod === PAYMENT_TYPES.TRANSFER_USD || paymentMethod === PAYMENT_TYPES.INTERNATIONAL_CARD) {
    if (billingCountryName === 'Estados Unidos de América') return '';
    return billingCountryName ?? '';
  }

  if (billingProvider === TYPES_OF_BILLING.STRIPE_USA_NUDOS_LLC) {
    return 'LLC';
  }

  if (billingProvider === TYPES_OF_BILLING.STRIPE_USA_NUDOS_CORP) {
    return 'C-Corp';
  }

  return '';
};
